import { useSnapshot } from 'valtio';
import { serviceState } from '@modules/ServiceModule/workspace/lib/state';
import { ServiceUserUtils } from '@modules/ServiceModule/workspace/lib';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import usePermissions from '@app/permissions/usePermissions';
import { permissions } from '@app/permissions/permissions';
import { UserActivityStatus } from '@api/generated/graphql';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';

const CALL_TRANSFER_TO_ACTIVE_EMPLOYEES_PERMS = [
  permissions.general.ACTION_CALL_OPTIONS.CALL_TRANSFER_TO_ACTIVE_EMPLOYEES
    .VIEW,
];

/**
 * Returns true when the primary Call/Transfer action should be disabled
 * because the typed/pasted keypad number resolves to a known busy user and
 * the agent lacks the "Call or Transfer to Employees on a Call" permission.
 *
 * Fail-open: when the number does not match any known user, returns false
 * so legitimate external numbers are unaffected. This is the explicit
 * inversion of the Office-tab gate (which is fail-closed on unknown users)
 * — external DIDs are a normal keypad use case.
 */
export const useIsKeypadNumberBlockedByActivity = (number: string): boolean => {
  const { users } = useSnapshot(serviceState);
  const {
    [FeatureFlagKeys.featureKeypadTransferOutboundToUserWithActivity]:
      featureKeypadTransferOutboundToUserWithActivity,
  } = useFeatureFlags();
  const hasCallTransferToActiveEmployeesPermission = usePermissions(
    CALL_TRANSFER_TO_ACTIVE_EMPLOYEES_PERMS,
    'all',
  );

  // `=== false` (not `!hasPermission`) so an undefined permission value
  // during auth loading does not satisfy the gate.
  const shouldBlockActiveEmployees =
    !!featureKeypadTransferOutboundToUserWithActivity &&
    hasCallTransferToActiveEmployeesPermission === false;

  if (!shouldBlockActiveEmployees) return false;

  // Star-prefixed inputs (e.g. *67) are dial-codes, not DIDs.
  if (number.startsWith('*')) return false;

  const normalized = sanitizePhoneNumberTo10Digits(number);
  if (normalized.length !== 10) return false;

  const matchedUser = users.find((user) => {
    const phone = user.personalLine?.phoneNumber;
    if (!phone) return false;
    return sanitizePhoneNumberTo10Digits(phone) === normalized;
  });

  if (!matchedUser) return false;

  return (
    ServiceUserUtils.getActivityStatus(matchedUser) !== UserActivityStatus.None
  );
};
